import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs, addDoc, updateDoc, serverTimestamp, query, where, writeBatch } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import { encodeArticleTrackingPack } from '../utils/articleTrackingLink';
import { ArrowLeftIcon, EnvelopeIcon, ChatBubbleLeftRightIcon, UserCircleIcon } from '@heroicons/react/24/outline';

// Helper function to get video thumbnail URL
const getVideoThumbnail = (url) => {
  if (!url) return null;
  
  // YouTube
  if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
  }
  
  // Vimeo
  if (url.includes('vimeo.com/')) {
    const videoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
    if (videoId) {
      return `https://vumbnail.com/${videoId}.jpg`;
    }
  }
  
  return null;
};

const SendContentMessage = () => {
  const { discipleId, resourceId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [disciple, setDisciple] = useState(null);
  const [message, setMessage] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDisciple, setLoadingDisciple] = useState(true);
  const [messageType, setMessageType] = useState('sms');

  useEffect(() => {
    loadDiscipleData();
    if (resourceId) {
      loadResource(resourceId);
    }
  }, [discipleId, resourceId, currentUser]);

  const loadDiscipleData = async () => {
    try {
      const discipleDoc = await getDoc(doc(db, 'disciples', discipleId));
      if (discipleDoc.exists()) {
        setDisciple({ id: discipleDoc.id, ...discipleDoc.data() });
      }
    } catch (error) {
      console.error('Error loading disciple:', error);
      alert('Failed to load disciple information');
    } finally {
      setLoadingDisciple(false);
    }
  };

  const loadResource = async (id) => {
    try {
      if (!currentUser) return;

      // Get track ID from mentor details
      const userAppID = currentUser.appID || currentUser.uid;
      const mentorDetailsDoc = await getDoc(
        doc(db, 'disciples', discipleId, 'mentorDetails', userAppID)
      );

      if (!mentorDetailsDoc.exists()) {
        alert('Error', 'Mentor details not found');
        return;
      }

      const trackId = mentorDetailsDoc.data()?.subscribedContentTrackId;
      if (!trackId) {
        alert('Error', 'No content track subscription found');
        return;
      }

      // Load resource
      const resourceDoc = await getDoc(
        doc(db, 'contentTracks', trackId, 'resources', id)
      );

      if (resourceDoc.exists()) {
        setSelectedResource({ id: resourceDoc.id, ...resourceDoc.data() });
      }
    } catch (error) {
      console.error('Error loading resource:', error);
      alert('Failed to load content resource');
    }
  };

  const buildMessagePreview = () => {
    const parts = [];

    if (message.trim()) {
      parts.push(message.trim());
    }

    if (selectedResource) {
      let resourceLink = selectedResource.url || `https://together-on-mission-dev.web.app/content/${selectedResource.id}`;
      
      // If it's an article link (tmrsrc.com/article/xxx), add tracking parameters
      if (resourceLink && resourceLink.includes('/article/') && currentUser && discipleId) {
        const userAppID = currentUser.appID || currentUser.uid;
        const articleDomain = process.env.REACT_APP_ARTICLE_DOMAIN || 'https://tmrsrc.com';
        
        // Extract article ID from URL
        const articleMatch = resourceLink.match(/\/article\/([^/?]+)/);
        if (articleMatch) {
          const articleId = articleMatch[1];
          const params = new URLSearchParams({
            d: discipleId,
            m: userAppID,
            r: selectedResource.id,
          });
          const x = encodeArticleTrackingPack(discipleId, userAppID, selectedResource.id);
          if (x) {
            params.set('x', x);
          }
          const qs = params.toString();
          // Path /t/{pack}: survives clients that strip ?query= entirely. Hash + query still help other clients.
          const hash = x ? `#x=${encodeURIComponent(x)}` : '';
          resourceLink = x
            ? `${articleDomain}/article/${articleId}/t/${x}?${qs}${hash}`
            : `${articleDomain}/article/${articleId}?${qs}`;
        }
      }
      
      parts.push(`\n\n📚 ${selectedResource.title || 'Content'}`);
      parts.push(`🔗 ${resourceLink}`);
    }

    return parts.join('\n');
  };

  const saveMessageToFirestore = async (preview, type) => {
    if (!currentUser || !disciple) return;

    try {
      const userAppID = currentUser.appID || currentUser.uid;
      const messageData = {
        text: preview,
        type,
        createdAt: serverTimestamp(),
        discipleId,
        discipleName: disciple.name,
        resourceId: selectedResource?.id,
        resourceTitle: selectedResource?.title,
      };

      // Save to messages subcollection
      const messageRef = await addDoc(
        collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, type === 'sms' ? 'messagesSMS' : 'messages'),
        messageData
      );

      // Also log to history subcollection for DiscipleHistory screen
      await addDoc(
        collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'history'),
        {
          type: 'content',
          messageId: messageRef.id,
          resourceId: selectedResource?.id,
          resourceTitle: selectedResource?.title,
          text: preview,
          messageType: type,
          createdAt: serverTimestamp(),
        }
      );

      // Update lastInteractionAt in mentorDetails (same as SendMessageScreen)
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      await updateDoc(mentorDetailsRef, {
        lastInteractionAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error saving message:', error);
      throw error;
    }
  };

  // Archive all active reminders for a specific disciple (web version)
  const archiveRemindersForDisciple = async (userId, discipleId) => {
    try {
      const remindersQuery = query(
        collection(db, 'users', userId, 'reminderQueue'),
        where('discipleId', '==', discipleId),
        where('archived', '==', false),
        where('reminderStatus', 'in', ['sent', 'snoozed'])
      );

      const remindersSnapshot = await getDocs(remindersQuery);

      if (remindersSnapshot.empty) {
        console.log(`No active reminders to archive for disciple ${discipleId}`);
        return;
      }

      const batch = writeBatch(db);
      remindersSnapshot.docs.forEach((doc) => {
        batch.update(doc.ref, {
          archived: true,
          reminderStatus: 'completed',
          completedAt: serverTimestamp(),
        });
      });

      await batch.commit();
      console.log(`Archived ${remindersSnapshot.docs.length} reminders for disciple ${discipleId}`);
    } catch (error) {
      console.error('Error archiving reminders for disciple:', error);
      // Don't throw - reminder archiving is not critical
    }
  };

  // Update reminder dates (same as SendMessage.js)
  const updateReminderDates = async (discipleId, userAppID) => {
    try {
      const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      const mentorDoc = await getDoc(mentorRef);
      const currentData = mentorDoc.data();
      
      if (currentData?.reminderSettings) {
        const now = new Date();
        const frequency = currentData.reminderSettings.frequency || 7;
        const nextDate = new Date(now.getTime() + (frequency * 24 * 60 * 60 * 1000));

        await updateDoc(mentorRef, {
          'reminderSettings.lastMessageDate': now.toISOString(),
          'reminderSettings.nextReminderDate': nextDate.toISOString()
        });
      }
    } catch (error) {
      console.error('Error updating reminder dates:', error);
    }
  };

  const markResourceAsSent = async () => {
    if (!selectedResource || !currentUser) return;

    try {
      const userAppID = currentUser.appID || currentUser.uid;
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      const mentorDetailsDoc = await getDoc(mentorDetailsRef);

      if (!mentorDetailsDoc.exists()) return;

      const existingProgress = mentorDetailsDoc.data()?.contentTrackProgress;
      if (!existingProgress) {
        console.warn('No content track progress found. Cannot mark resource as sent.');
        return;
      }

      // Check if resource was already sent
      if (existingProgress.sentResourceIds?.includes(selectedResource.id)) {
        console.log('Resource already marked as sent:', selectedResource.id);
        return;
      }

      // Add resource ID to sent list
      const updatedSentResourceIds = [...(existingProgress.sentResourceIds || []), selectedResource.id];
      const updatedSentCount = updatedSentResourceIds.length;
      const updatedRemainingCount = existingProgress.totalPublishedResources - updatedSentCount;
      const updatedCompleted = existingProgress.totalPublishedResources > 0 && updatedRemainingCount === 0;

      const updatedProgress = {
        ...existingProgress,
        sentResourceIds: updatedSentResourceIds,
        sentCount: updatedSentCount,
        remainingCount: updatedRemainingCount,
        completed: updatedCompleted,
        lastUpdated: serverTimestamp(),
      };

      // Update in Firestore
      await updateDoc(mentorDetailsRef, {
        contentTrackProgress: updatedProgress,
      });
    } catch (error) {
      console.error('Error marking resource as sent:', error);
    }
  };

  const handleSendSMS = async () => {
    if (!message.trim() && !selectedResource) {
      alert('Please enter a message or select content to send');
      return;
    }

    if (!disciple?.phone) {
      alert('This disciple does not have a phone number. Please add a phone number in Disciple Settings.');
      return;
    }

    setLoading(true);
    try {
      const preview = buildMessagePreview();

      // Save to Firestore first
      await saveMessageToFirestore(preview, 'sms');

      // Mark resource as sent
      await markResourceAsSent();

      // Archive reminders for this disciple
      if (currentUser) {
        try {
          await archiveRemindersForDisciple(currentUser.uid, discipleId);
        } catch (error) {
          console.error('Error archiving reminders:', error);
          // Don't block message sending if reminder archiving fails
        }
      }

      // Update reminder dates
      if (currentUser) {
        try {
          const userAppID = currentUser.appID || currentUser.uid;
          await updateReminderDates(discipleId, userAppID);
        } catch (error) {
          console.error('Error updating reminder dates:', error);
        }
      }

      // Clean phone number
      let cleanPhone = disciple.phone.replace(/\D/g, '');
      if (cleanPhone.startsWith('1') && cleanPhone.length === 11) {
        cleanPhone = cleanPhone.slice(1);
      }

      if (cleanPhone.length !== 10) {
        alert(`Invalid phone number format (${cleanPhone.length} digits). Please update the disciple's phone number in settings.`);
        setLoading(false);
        return;
      }

      // Format SMS URL
      const formattedMessage = preview;
      const smsUrl = `sms:+1${cleanPhone}?body=${encodeURIComponent(formattedMessage)}`;
      
      // Debug: Log the message preview to verify tracking parameters are included
      // Copy to clipboard as fallback for desktop
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(formattedMessage);
          alert('Message copied to clipboard! Open your SMS app and paste it.');
        } catch (err) {
          console.error('Failed to copy to clipboard:', err);
        }
      }

      // Try to open SMS link (works on mobile)
      window.location.href = smsUrl;
      
      // Navigate back after a delay
      setTimeout(() => {
        navigate(`/disciple-menu/${discipleId}`);
      }, 1000);
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send SMS. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!message.trim() && !selectedResource) {
      alert('Please enter a message or select content to send');
      return;
    }

    if (!disciple?.email) {
      alert('This disciple does not have an email address. Please add an email in Disciple Settings.');
      return;
    }

    setLoading(true);
    try {
      const preview = buildMessagePreview();

      // Save to Firestore
      await saveMessageToFirestore(preview, 'email');

      // Mark resource as sent
      await markResourceAsSent();

      // Archive reminders for this disciple
      if (currentUser) {
        try {
          await archiveRemindersForDisciple(currentUser.uid, discipleId);
        } catch (error) {
          console.error('Error archiving reminders:', error);
          // Don't block message sending if reminder archiving fails
        }
      }

      // Update reminder dates
      if (currentUser) {
        try {
          const userAppID = currentUser.appID || currentUser.uid;
          await updateReminderDates(discipleId, userAppID);
        } catch (error) {
          console.error('Error updating reminder dates:', error);
        }
      }

      // Open email app
      const subject = encodeURIComponent(`Content: ${selectedResource?.title || 'From Together On Mission'}`);
      const body = encodeURIComponent(preview);
      const emailUrl = `mailto:${disciple.email}?subject=${subject}&body=${body}`;

      window.location.href = emailUrl;
      
      // Navigate back after a delay
      setTimeout(() => {
        navigate(`/disciple-menu/${discipleId}`);
      }, 1000);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingDisciple) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const videoUrl = selectedResource?.url || selectedResource?.videoUrl || selectedResource?.link;
  const isVideo = selectedResource?.type === 'video' || 
                 (videoUrl && (videoUrl.includes('youtube') || videoUrl.includes('youtu.be') || videoUrl.includes('vimeo')));
  const thumbnailUrl = isVideo && videoUrl ? getVideoThumbnail(videoUrl) : null;

  const firstName = disciple?.name?.split(' ')[0] || '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — match SendMessage / mobile */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center min-w-0 flex-1">
            <button
              onClick={() => navigate(-1)}
              className="mr-3 p-1 rounded-full hover:bg-gray-100"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              <UserCircleIcon className="h-8 w-8 text-gray-400" />
            </div>
            <div className="ml-3 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                Send content to {firstName}
              </h1>
              <p className="text-sm text-gray-500 truncate">
                {selectedResource?.title || 'Content'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Message type pills (mobile: SMS = green when active, Email = blue) */}
        <div className="flex bg-white rounded-xl p-1.5 border border-gray-200 shadow-sm mb-4">
          <button
            type="button"
            onClick={() => setMessageType('sms')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition ${
              messageType === 'sms'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            SMS
          </button>
          <button
            type="button"
            onClick={() => setMessageType('email')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition ${
              messageType === 'email'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <EnvelopeIcon className="h-5 w-5" />
            Email
          </button>
        </div>

        {/* Selected content card (mobile-style) */}
        {selectedResource && (
          <div className="bg-white rounded-xl p-4 mb-4 border border-gray-200 shadow-sm overflow-hidden">
            {thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt={selectedResource.title}
                className="w-full h-40 object-cover rounded-lg mb-3"
              />
            )}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-xl">📚</span>
              {selectedResource.type && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-blue-50 text-xs font-semibold text-blue-700 capitalize">
                  {selectedResource.type}
                </span>
              )}
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {selectedResource.title || 'Content'}
            </h3>
            {selectedResource.url && (
              <p className="text-xs text-gray-500 truncate">{selectedResource.url}</p>
            )}
          </div>
        )}

        {/* Personal message (optional) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Personal message (optional)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a personal message to accompany the content..."
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </div>

        {/* Message preview */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Message preview
          </p>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {buildMessagePreview() || 'Enter a message or select content...'}
            </p>
          </div>
        </div>

        {/* Send button */}
        <div className="w-full">
          {messageType === 'sms' ? (
            <button
              onClick={handleSendSMS}
              disabled={loading || (!message.trim() && !selectedResource)}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3.5 px-6 rounded-lg transition shadow-sm"
            >
              {loading ? 'Sending...' : 'Send SMS'}
            </button>
          ) : (
            <button
              onClick={handleSendEmail}
              disabled={loading || (!message.trim() && !selectedResource)}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3.5 px-6 rounded-lg transition shadow-sm"
            >
              {loading ? 'Sending...' : 'Send Email'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendContentMessage;




